import { Component, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { SubmissionService } from '../../../shared/submission.service';
import { MarketVacancyService } from '../../../shared/market-vacancy.service';
import { User } from '../../../interfaces/user';
import {Globals} from "../../../shared/Globals";

/**
 * Add a consultant to an Opportunity as a Submission. Uses the shared
 * `bst-list-adder` (ES-backed search, target='member') with a custom
 * `listTemplate` that shows the consultant name and their vacancy match-% badge
 * when a score exists. `oppId`, `existingConsultantIds` (drives the duplicate
 * warning), and optional `vacancyId` are supplied via the NbDialog `context`.
 * Closes with the created Submission object, or null on cancel.
 *
 * When a vacancy is linked, results re-sort by the server's match rank via the
 * list-adder's sortResults hook (eligibility tier first, then score, so a
 * demoted high scorer never tops the list); unranked users keep ES-relevance
 * order after the ranked ones.
 */
@Component({
  selector: 'bst-add-submission-dialog',
  templateUrl: './add-submission-dialog.component.html',
  styleUrls: ['./add-submission-dialog.component.scss'],
  standalone: false,
})
export class AddSubmissionDialogComponent implements OnInit {
  // Supplied via dialog context.
  oppId!: number;
  /** Consultants already submitted to this opportunity: drives the proactive
   *  duplicate warning (the server would 409 anyway; this warns BEFORE). */
  existingConsultantIds: number[] = [];
  /** Optional — when provided, match scores are loaded and shown in the picker. */
  vacancyId?: number | null;

  selectedConsultant: User | null = null;
  saving = false;
  errorMessage: string | null = null;

  /** User_id -> rounded match score (0-100). Populated on init when vacancyId is set. */
  scoreMap = new Map<number, number>();
  /** User_id -> position in the server's matching_users order (tier, then score). */
  rankMap = new Map<number, number>();

  constructor(
    private ref: NbDialogRef<AddSubmissionDialogComponent>,
    private submissionService: SubmissionService,
    private marketVacancyService: MarketVacancyService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    if (this.vacancyId) {
      this.marketVacancyService.getMatchingUsers(this.vacancyId, 100).then(resp => {
        const ranked = resp?.matching_users ?? [];
        this.scoreMap = new Map(
          ranked
            .filter(u => u.score != null)
            .map(u => [u.User_id, Math.round(u.score as number)])
        );
        this.rankMap = new Map(ranked.map((u, i) => [u.User_id, i]));
      }).catch(() => {
        // Non-fatal: badge simply won't show if scores fail to load.
      });
    }
  }

  /** Returns the rounded match score for a user, or null if not available. */
  score(user: User): number | null {
    const s = this.scoreMap.get(user?.id);
    return s != null ? s : null;
  }

  /** Server match rank first; unranked users keep their ES order at the end. */
  byRank = (a: User, b: User): number =>
    (this.rankMap.get(a?.id) ?? Infinity) - (this.rankMap.get(b?.id) ?? Infinity) || 0;

  get consultantId(): number | null {
    return this.selectedConsultant?.id ?? null;
  }

  /** True when the picked consultant is already submitted to this opportunity. */
  get isDuplicate(): boolean {
    return this.consultantId != null && this.existingConsultantIds.includes(this.consultantId);
  }

  get canSubmit(): boolean {
    return !this.saving && !!this.consultantId && !this.isDuplicate;
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) { return; }
    this.saving = true;
    this.errorMessage = null;
    try {
      const created = await this.submissionService.create(this.oppId, this.consultantId!);
      this.ref.close(created);
    } catch (e: any) {
      console.error('submission create failed', e);
      const status = e?.status ?? e?.error?.status;
      if (status === 409 || e?.error?.message?.toLowerCase().includes('already')) {
        this.errorMessage = this.translate.instant('crm.dup_consultant');
      } else {
        this.errorMessage = e?.error?.message ?? this.translate.instant('crm.err.add_submission');
      }
      this.saving = false;
    }
  }

  cancel(): void {
    this.ref.close(null);
  }

  protected readonly Globals = Globals;
}
